import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Card, Button, Form, Table } from 'react-bootstrap';
import useAuth from '../../pages/useAuth';
import PageLoder from '../../components/PageLoader';

// ✅ Module-level cache for ongoing fetches (persists across re-renders)
const _ongoingFetches = new Map();

const SmallCard = ({ title, children, bg }) => (
     <div style={{
          background: bg || '#fff',
          borderRadius: 10,
          padding: 12,
          minWidth: 160,
          flex: '1 1 160px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
     }}>
          <div style={{ fontSize: 13, color: '#444', marginBottom: 8 }}>{title}</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{children}</div>
     </div>
);

const StatCard = ({ title, value, color = '#fff', active, onClick }) => (
     <Card onClick={onClick} style={{
          cursor: onClick ? 'pointer' : 'default',
          borderRadius: 8,
          border: active ? '2px solid #4f46e5' : '1px solid #eee',
          padding: 8,
          boxShadow: 'none',
          background: color
     }}>
          <Card.Body style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: 8 }}>
               <div style={{ lineHeight: 1 }}>
                    <div style={{ fontSize: 18, color: '#666' }}>{title}</div>
               </div>
               <div style={{
                    minWidth: 44,
                    height: 44,
                    borderRadius: 8,
                    background: '#fff',
                    border: '1px solid #b1b1b1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700
               }}>
                    {value ?? '—'}
               </div>
          </Card.Body>
     </Card>
);

// ✅ Mobile Card for each employee
const MobileEmployeeCard = ({ employee, index, selectedDate }) => (
     <Card className="mb-2" style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <Card.Body style={{ padding: '12px' }}>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                    <div>
                         <div style={{ fontSize: 16, fontWeight: 600, color: '#333' }}>
                              {employee.employee_name}
                         </div>
                         <div style={{ fontSize: 13, color: '#666' }}>
                              {employee.employee_code}
                         </div>
                    </div>
                    <span
                         className={`badge ${employee.status === 'present' ? 'bg-success' :
                              employee.status === 'absent' ? 'bg-danger' :
                                   'bg-secondary'
                              }`}
                         style={{ fontSize: 11, padding: '4px 8px' }}
                    >
                         {employee.status || '—'}
                    </span>
               </div>

               {employee.shift_details && (
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
                         <strong>Shift:</strong> {employee.shift_details.shift_name}
                         <span style={{ color: '#999', marginLeft: 8 }}>
                              ({employee.shift_details.in_time?.substring(0, 5)} - {employee.shift_details.out_time?.substring(0, 5)})
                         </span>
                    </div>
               )}

               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                    <div>
                         <div style={{ color: '#666', marginBottom: 2 }}>Punch In</div>
                         <div style={{ fontWeight: 600 }}>
                              {employee.punch_in
                                   ? new Date(employee.punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                   : '-'}
                         </div>
                    </div>
                    <div>
                         <div style={{ color: '#666', marginBottom: 2 }}>Punch Out</div>
                         <div style={{ fontWeight: 600 }}>
                              {employee.punch_in && employee.punch_out && employee.punch_in === employee.punch_out && selectedDate === new Date().toISOString().split('T')[0]
                                   ? <span style={{ color: '#28a745' }}>On Duty</span>
                                   : employee.punch_out
                                        ? new Date(employee.punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                        : '-'}
                         </div>
                    </div>
               </div>
          </Card.Body>
     </Card>
);

const SuperAdmin_Dashboard = () => {
     useAuth();
     const baseUrl = useMemo(() => process.env.REACT_APP_BASE_URL, []);
     const [loading, setLoading] = useState(true);
     const [summary, setSummary] = useState({ total: 0, present: 0, on_leave: 0, absent: 0 });
     const [doctors, setDoctors] = useState([]);
     const [filter, setFilter] = useState('all');
     const [q, setQ] = useState('');
     const [page, setPage] = useState(1);
     const [pageSize, setPageSize] = useState(20);
     const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
     const [selectedDate, setSelectedDate] = useState(() => {
          const today = new Date();
          const yyyy = today.getFullYear();
          const mm = String(today.getMonth() + 1).padStart(2, '0');
          const dd = String(today.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
     });
     const [empType, setEmpType] = useState('DOCTOR');

     // ✅ Handle window resize
     useEffect(() => {
          const handleResize = () => setIsMobile(window.innerWidth < 768);
          window.addEventListener('resize', handleResize);
          return () => window.removeEventListener('resize', handleResize);
     }, []);

     // ✅ Fetch doctor summary with deduplication
     useEffect(() => {
          console.log('🔵 useEffect RUNNING with:', { selectedDate, empType });

          let mounted = true;

          const fetchData = async () => {
               setLoading(true);
               try {
                    let url = `${baseUrl}/doctorSummary`;
                    const params = new URLSearchParams();
                    if (selectedDate) params.append('date', selectedDate);
                    if (empType) params.append('emp_type', empType);
                    if (params.toString()) url += `?${params.toString()}`;

                    if (_ongoingFetches.has(url)) {
                         console.log('⏳ Reusing ongoing fetch for:', url);
                         try {
                              const result = await _ongoingFetches.get(url);
                              if (!mounted) return;

                              if (result && result.status && result.data) {
                                   const s = result.data.summary || {};
                                   setSummary({
                                        total: s.total ?? 0,
                                        present: s.present ?? 0,
                                        on_leave: s.on_leave ?? 0,
                                        absent: s.absent ?? 0,
                                   });
                                   setDoctors(Array.isArray(result.data.details) ? result.data.details : []);
                              } else {
                                   setSummary({ total: 0, present: 0, on_leave: 0, absent: 0 });
                                   setDoctors([]);
                              }
                         } catch (err) {
                              console.error('Shared fetch error:', err);
                              if (mounted) {
                                   setSummary({ total: 0, present: 0, on_leave: 0, absent: 0 });
                                   setDoctors([]);
                              }
                         } finally {
                              if (mounted) setLoading(false);
                         }
                         return;
                    }

                    console.log('🟢 Starting new fetch for:', url);
                    const fetchPromise = (async () => {
                         const res = await fetch(url, {
                              method: 'GET',
                              headers: {
                                   'Content-Type': 'application/json',
                                   'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                              },
                         });

                         if (!res.ok) {
                              throw new Error(`HTTP ${res.status}: ${res.statusText}`);
                         }

                         return await res.json();
                    })();

                    _ongoingFetches.set(url, fetchPromise);

                    const j = await fetchPromise;
                    if (!mounted) return;

                    if (j && j.status && j.data) {
                         const s = j.data.summary || {};
                         setSummary({
                              total: s.total ?? 0,
                              present: s.present ?? 0,
                              on_leave: s.on_leave ?? 0,
                              absent: s.absent ?? 0,
                         });
                         setDoctors(Array.isArray(j.data.details) ? j.data.details : []);
                    } else {
                         setSummary({ total: 0, present: 0, on_leave: 0, absent: 0 });
                         setDoctors([]);
                    }
               } catch (err) {
                    console.error('Doctor summary fetch error:', err);
                    if (mounted) {
                         setSummary({ total: 0, present: 0, on_leave: 0, absent: 0 });
                         setDoctors([]);
                    }
               } finally {
                    let url = `${baseUrl}/doctorSummary`;
                    const params = new URLSearchParams();
                    if (selectedDate) params.append('date', selectedDate);
                    if (empType) params.append('emp_type', empType);
                    if (params.toString()) url += `?${params.toString()}`;
                    _ongoingFetches.delete(url);

                    if (mounted) setLoading(false);
               }
          };

          fetchData();

          return () => {
               console.log('🔴 useEffect CLEANUP');
               mounted = false;
          };
     }, [baseUrl, selectedDate, empType]);

     const filtered = useMemo(() => {
          const qlow = (q || '').trim().toLowerCase();
          return doctors.filter(d => {
               if (!d) return false;
               if (filter !== 'all' && (d.status || '').toLowerCase() !== filter) return false;
               if (!qlow) return true;
               return (
                    (d.employee_name || '').toLowerCase().includes(qlow) ||
                    (d.employee_code || '').toLowerCase().includes(qlow)
               );
          });
     }, [doctors, filter, q]);

     const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
     const pageClamped = Math.min(Math.max(1, page), totalPages);

     useEffect(() => {
          setPage(1);
     }, [filter, q, pageSize]);

     const pageItems = filtered.slice((pageClamped - 1) * pageSize, pageClamped * pageSize);

     const maxBtns = isMobile ? 5 : 10;
     let start = 1;
     let end = totalPages;
     if (totalPages > maxBtns) {
          start = Math.max(1, pageClamped - Math.floor(maxBtns / 2));
          end = start + maxBtns - 1;
          if (end > totalPages) {
               end = totalPages;
               start = Math.max(1, end - maxBtns + 1);
          }
     }

     useEffect(() => {
          console.log('🟢 SuperAdmin_Dashboard MOUNTED');
          return () => console.log('🔴 SuperAdmin_Dashboard UNMOUNTED');
     }, []);

     return (
          <div style={{ padding: isMobile ? 12 : 24, backgroundColor: '#f7f9fc', minHeight: '100vh' }}>
               <div style={{ margin: '0 auto' }}>

                    {/* Employee Type Selector & Stats */}
                    <Row className="mb-3">
                         <Col xs={12} md={3} className="mb-2 mb-md-0">
                              <Form.Select
                                   value={empType}
                                   onChange={e => setEmpType(e.target.value)}
                                   size={isMobile ? "sm" : "md"}
                              >
                                   <option value="DOCTOR">DOCTORS</option>
                                   <option value="CONTRACTUAL_EMPLOYEE">CONTRACTUAL EMPLOYEE</option>
                                   <option value="POOJARI">POOJARI</option>
                              </Form.Select>
                         </Col>
                         <Col xs={12} md={9}>
                              <div style={{
                                   display: 'grid',
                                   gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(170px, 1fr))',
                                   gap: isMobile ? 8 : 10
                              }}>
                                   <StatCard
                                        title={isMobile ? 'Total' : `Total ${empType === 'DOCTOR' ? 'Doctors' : empType === 'CONTRACTUAL_EMPLOYEE' ? 'Employees' : 'Poojaris'}`}
                                        value={loading ? '…' : summary.total}
                                        color="#e8f0fe"
                                        active={filter === 'all'}
                                        onClick={() => setFilter('all')}
                                   />
                                   <StatCard
                                        title="Present"
                                        value={loading ? '…' : summary.present}
                                        color="#e9f7ef"
                                        active={filter === 'present'}
                                        onClick={() => setFilter('present')}
                                   />
                                   <StatCard
                                        title="Absent"
                                        value={loading ? '…' : summary.absent}
                                        color="#fff7e6"
                                        active={filter === 'absent'}
                                        onClick={() => setFilter('absent')}
                                   />
                                   <StatCard
                                        title="On Leave"
                                        value={loading ? '…' : summary.on_leave}
                                        color="#fff1f0"
                                        active={filter === 'on_leave'}
                                        onClick={() => setFilter('on_leave')}
                                   />
                              </div>
                         </Col>
                    </Row>

                    {/* Filters */}
                    <Row className="mb-2" style={{ alignItems: 'center' }}>
                         <Col xs={12} md={3} className="mb-2 mb-md-0">
                              <Form.Control
                                   type="date"
                                   value={selectedDate}
                                   onChange={e => setSelectedDate(e.target.value)}
                                   size="sm"
                              />
                         </Col>
                         <Col xs={12} md={6} className="mb-2 mb-md-0">
                              <Form.Control
                                   placeholder="Search by name or code"
                                   value={q}
                                   onChange={e => setQ(e.target.value)}
                                   size="sm"
                              />
                         </Col>
                         <Col xs={12} md={3} className="text-md-end">
                              <Form.Select
                                   value={pageSize}
                                   onChange={e => setPageSize(Number(e.target.value))}
                                   style={{ width: isMobile ? '100%' : 120, display: isMobile ? 'block' : 'inline-block' }}
                                   size="sm"
                              >
                                   <option value={10}>10 / page</option>
                                   <option value={20}>20 / page</option>
                                   <option value={50}>50 / page</option>
                              </Form.Select>
                         </Col>
                    </Row>

                    {/* Desktop Table / Mobile Cards */}
                    {isMobile ? (
                         // ✅ Mobile View - Cards
                         <div>
                              {loading ? (
                                   <div className="text-center py-4">
                                        <PageLoder />
                                   </div>
                              ) : pageItems.length === 0 ? (
                                   <Card className="text-center py-4">
                                        <Card.Body>No records found</Card.Body>
                                   </Card>
                              ) : (
                                   pageItems.map((d, idx) => (
                                        <MobileEmployeeCard
                                             key={d.emp_id || d.employee_code || idx}
                                             employee={d}
                                             index={(pageClamped - 1) * pageSize + idx + 1}
                                             selectedDate={selectedDate}
                                        />
                                   ))
                              )}
                         </div>
                    ) : (
                         // ✅ Desktop View - Table
                         <Card className="mb-2" style={{ borderRadius: 8 }}>
                              <Card.Body style={{ padding: 0 }}>
                                   <Table hover responsive size="sm" className="mb-0">
                                        <thead>
                                             <tr style={{ fontSize: 13 }}>
                                                  <th style={{ width: 40 }}>#</th>
                                                  <th style={{ width: 120 }}>Code</th>
                                                  <th>Name</th>
                                                  <th style={{ width: 200 }}>Shift</th>
                                                  <th style={{ width: 200 }}>Punch In</th>
                                                  <th style={{ width: 200 }}>Punch Out</th>
                                                  <th style={{ width: 100 }}>Status</th>
                                             </tr>
                                        </thead>
                                        <tbody>
                                             {loading ? (
                                                  <tr><td colSpan="7" className="text-center py-3">
                                                       <PageLoder />
                                                  </td></tr>
                                             ) : pageItems.length === 0 ? (
                                                  <tr><td colSpan="7" className="text-center py-3">No records found</td></tr>
                                             ) : (
                                                  pageItems.map((d, idx) => (
                                                       <tr key={d.emp_id || d.employee_code || idx}>
                                                            <td style={{ verticalAlign: 'middle' }}>
                                                                 {(pageClamped - 1) * pageSize + idx + 1}
                                                            </td>
                                                            <td style={{ verticalAlign: 'middle' }}>{d.employee_code}</td>
                                                            <td style={{ verticalAlign: 'middle' }}>{d.employee_name}</td>
                                                            <td style={{ verticalAlign: 'middle' }}>
                                                                 {d.shift_details ? (
                                                                      <div style={{ fontSize: 12 }}>
                                                                           <div style={{ fontWeight: 600 }}>
                                                                                {d.shift_details.shift_name}
                                                                           </div>
                                                                           <div style={{ color: '#666', fontSize: 11 }}>
                                                                                {d.shift_details.in_time?.substring(0, 5)} - {d.shift_details.out_time?.substring(0, 5)}
                                                                           </div>
                                                                      </div>
                                                                 ) : (
                                                                      <span style={{ color: '#999' }}>-</span>
                                                                 )}
                                                            </td>
                                                            <td style={{ verticalAlign: 'middle' }}>
                                                                 {d.punch_in
                                                                      ? new Date(d.punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                                      : '-'}
                                                            </td>
                                                            <td style={{ verticalAlign: 'middle' }}>
                                                                 {d.punch_in && d.punch_out && d.punch_in === d.punch_out && selectedDate === new Date().toISOString().split('T')[0]
                                                                      ? <span style={{ color: '#28a745', fontWeight: 600, fontSize: 11 }}>On Duty</span>
                                                                      : d.punch_out
                                                                           ? new Date(d.punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                                           : '-'}
                                                            </td>
                                                            <td style={{ verticalAlign: 'middle' }}>
                                                                 <span
                                                                      className={`badge ${d.status === 'present' ? 'bg-success' :
                                                                           d.status === 'absent' ? 'bg-danger' :
                                                                                'bg-secondary'
                                                                           }`}
                                                                      style={{ textTransform: 'capitalize', fontSize: 12, padding: '4px 8px' }}
                                                                 >
                                                                      {d.status || '—'}
                                                                 </span>
                                                            </td>
                                                       </tr>
                                                  ))
                                             )}
                                        </tbody>
                                   </Table>
                              </Card.Body>
                         </Card>
                    )}

                    {/* Pagination */}
                    <div style={{
                         display: 'flex',
                         flexDirection: isMobile ? 'column' : 'row',
                         justifyContent: 'space-between',
                         alignItems: isMobile ? 'stretch' : 'center',
                         gap: 12,
                         marginTop: 8
                    }}>
                         <div style={{ fontSize: 13, color: '#555', textAlign: isMobile ? 'center' : 'left' }}>
                              Showing {filtered.length === 0 ? 0 : (pageClamped - 1) * pageSize + 1} - {Math.min(pageClamped * pageSize, filtered.length)} of {filtered.length}
                         </div>

                         <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: isMobile ? 'center' : 'flex-end', flexWrap: 'wrap' }}>
                              {Array.from({ length: end - start + 1 }).map((_, i) => {
                                   const pg = start + i;
                                   return (
                                        <button
                                             key={pg}
                                             onClick={() => setPage(pg)}
                                             style={{
                                                  padding: isMobile ? '6px 10px' : '4px 10px',
                                                  border: '1px solid #ddd',
                                                  borderRadius: 4,
                                                  cursor: 'pointer',
                                                  backgroundColor: pg === pageClamped ? '#007bff' : '#fff',
                                                  color: pg === pageClamped ? '#fff' : '#333',
                                                  fontSize: 13
                                             }}
                                        >
                                             {pg}
                                        </button>
                                   );
                              })}
                         </div>
                    </div>
               </div>
          </div>
     );
};

export default SuperAdmin_Dashboard;